import { useState, useMemo, useCallback } from 'react';

/**
 * Hromadný výběr položek v seznamu.
 *
 * Použití:
 *   const sel = useBulkSelect(items.map(i => i.id));
 *   sel.isSelected(id) / sel.toggle(id) / sel.toggleAll() / sel.clear()
 *   sel.selectedIds (pole) / sel.count / sel.allSelected / sel.someSelected
 *
 * `allIds` je aktuální seznam viditelných id (např. po filtraci) — toggleAll a
 * allSelected se počítají proti němu, výběr se drží nezávisle (Set).
 */
export default function useBulkSelect(allIds = []) {
  const [selected, setSelected] = useState(() => new Set());

  const ids = useMemo(() => allIds.filter(Boolean).map(String), [allIds]);

  const isSelected = useCallback((id) => selected.has(String(id)), [selected]);

  const toggle = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const allSelected = ids.length > 0 && ids.every(id => selected.has(id));
  const someSelected = ids.some(id => selected.has(id)) && !allSelected;

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const allOn = ids.length > 0 && ids.every(id => prev.has(id));
      return allOn ? new Set() : new Set(ids);
    });
  }, [ids]);

  // Vybrané id-čka omezená jen na aktuálně viditelná (bezpečné pro akce).
  const selectedIds = useMemo(() => ids.filter(id => selected.has(id)), [ids, selected]);

  return {
    selectedIds,
    count: selectedIds.length,
    isSelected,
    toggle,
    toggleAll,
    clear,
    allSelected,
    someSelected,
  };
}

/**
 * Hromadné smazání přes existující single-DELETE endpoint.
 * `deleteOne(id)` = funkce vracející Promise (např. id => api.delete(`/companies/${id}`)).
 * Vrací { ok, failed }.
 */
export async function bulkDelete(ids, deleteOne) {
  const results = await Promise.allSettled(ids.map(id => deleteOne(id)));
  const ok = results.filter(r => r.status === 'fulfilled').length;
  return { ok, failed: results.length - ok };
}
